'use client';

import { Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import GlobalBlockRenderer, {
  sharedOverrides,
} from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksThreeColumnText,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

const headingFontMap: Record<string, string> = {
  xs: '1rem',
  sm: '1.25rem',
  md: '1.6rem',
  lg: '2rem',
  xl: '3.5rem',
};

export function ThreeColumnText({ blocks }: { blocks: ComponentBlocksThreeColumnText }) {
  const variant = blocks.ThreeColumnVariant ?? 'card';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasContent = (content: any) => {
    if (!content || !Array.isArray(content)) return false;
    return content.some((block) => {
      if (!block.children || !Array.isArray(block.children)) return false;
      return block.children.some((child: any) => {
        return child.text && child.text.trim().length > 0;
      });
    });
  };

  const columns = [
    {
      content: blocks.LeftContent,
      image: blocks.LeftImage,
      headingSize: blocks.LeftHeadingFontSize ?? 'md',
      bodySize: blocks.LeftBodyFontSize ?? 'md',
    },
    {
      content: blocks.CenterContent,
      image: blocks.CenterImage,
      headingSize: blocks.CenterHeadingFontSize ?? 'md',
      bodySize: blocks.CenterBodyFontSize ?? 'md',
    },
    {
      content: blocks.RightContent,
      image: blocks.RightImage,
      headingSize: blocks.RightHeadingFontSize ?? 'md',
      bodySize: blocks.RightBodyFontSize ?? 'md',
    },
  ].filter((c) => hasContent(c.content) || c.image);

  // Adjust spacing based on number of columns
  const getSpacing = () => {
    const columnCount = columns.length;
    if (columnCount === 1) {
      return isMobile ? 0 : 0;
    } else if (columnCount === 2) {
      return isMobile ? 5 : 4;
    }
    return isMobile ? 4 : 3;
  };

  return (
    <CustomBackground
      backgroundOptions={(blocks?.ThreeColumnBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (blocks?.ThreeColumnDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'ThreeColumnText',
      }}
    >
      {blocks.ThreeColumnTitle && (
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <ParsePillHeading title={blocks.ThreeColumnTitle} />
        </Box>
      )}{' '}
      {blocks.ThreeColumnDescription && (
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <GlobalBlockRenderer content={blocks.ThreeColumnDescription || []} />
        </Box>
      )}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="center"
        alignItems="stretch"
        spacing={getSpacing()}
        className={`ThreeColumnVariant-${variant}`}
        sx={{
          width: '100%',
          mx: 'auto',
          boxSizing: 'border-box',
        }}
      >
        {columns.map((col, index) => (
          <Box
            key={index}
            sx={{
              flex: columns.length === 3 ? '1 1 0%' : '0 1 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              minWidth: 0,
              maxWidth:
                columns.length === 1
                  ? { xs: '100%', md: '600px' }
                  : columns.length === 2
                    ? { xs: '100%', md: '350px' }
                    : '100%',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1,
                '&.ThreeColumnVariant-card': {
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  textAlign: 'center',
                  background: theme.palette.common.white,
                  height: '100%',
                  p: { xs: 3, md: 4 },
                  borderRadius: 2,
                  border: '2px solid #9289D8',
                  boxShadow: '5px 5px 0 #9289D8',
                  transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                  '&:hover': {
                    boxShadow: '7px 7px 0 #9289D8',
                    transform: { xs: 'none', md: 'translateY(-3px)' },
                  },
                },
              }}
            >
              {col.image && (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    overflow: 'hidden',
                  }}
                  className="strapi-image-box"
                >
                  <StrapiImage image={col.image} size="logo" imageWidth={64} imageHeight={64} />
                </Box>
              )}

              <Box
                sx={{
                  fontSize: headingFontMap[col.bodySize] || 'inherit',
                  'h1,h2,h3,h4,h5,h6': {
                    fontSize: headingFontMap[col.headingSize] || 'inherit',
                    marginBottom: `calc(${headingFontMap[col.headingSize]} / 4)`,
                    '& + p': {
                      marginTop: 0,
                    },
                  },
                }}
              >
                <GlobalBlockRenderer
                  content={col.content || []}
                  overrides={
                    variant === 'card'
                      ? {
                          blocks: {
                            heading: ({ children }: { children?: React.ReactNode }) => (
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                gutterBottom
                                sx={{
                                  color: theme.palette.purple[800],
                                }}
                              >
                                {children}
                              </Typography>
                            ),
                            ...sharedOverrides.simpleLink,
                          },
                        }
                      : {
                          blocks: {
                            ...sharedOverrides.simpleLink,
                          },
                        }
                  }
                />
              </Box>
            </Paper>
          </Box>
        ))}
      </Stack>
    </CustomBackground>
  );
}
